/**
 * @file PublishChecksList.tsx
 * @description Проверки перед публикацией и возвратом версии (phase19.16 Р1;
 * uiux-design-r1 §6; SB59, SB74, SB49): «✓ Сведения о продавце заполнены»,
 * «! Пункт «Консультации» скрыт…», «✕ Не заполнены сведения о продавце…
 * [Заполнить реквизиты →]». Значок и текст — не только цвет.
 *
 * phase19.16 Р2а: проверки шаблона страницы товара (409
 * `site_view_publish_blocked`) — ссылка «Открыть шаблон» и, у пропавшего или
 * скрытого обязательного элемента, кнопка «Вернуть элемент» (`onRestore`).
 * Куда ведут ссылки — реестр `lib/site-view/publish-check-links` (общий с
 * плашкой «Сайт работает без: …», SB125).
 */

import { AlertCircle, CheckCircle2, TriangleAlert, type LucideIcon } from "lucide-react";
import type { ProductRequiredKind } from "@chaiberi/shared";
import { Link } from "react-router";

import { TemplateIssueList } from "./TemplateIssueList";

import { useCurrentContext } from "../../../auth/hooks";
import { useBuilderLink } from "../../../hooks/site-builder/useBuilderLink";
import { checkLinkMeta } from "../../../lib/site-view/publish-check-links";
import type { PublishCheck } from "../../../lib/site-view/types";
import { cn } from "@/lib/utils";

/** Уровень проверки → значок, цвет, подпись для скринридера. */
const LEVEL_META: Readonly<Record<PublishCheck["level"], { icon: LucideIcon; className: string; sr: string }>> = {
  ok: { icon: CheckCircle2, className: "text-success", sr: "Готово" },
  warning: { icon: TriangleAlert, className: "text-warning", sr: "Предупреждение" },
  error: { icon: AlertCircle, className: "text-destructive", sr: "Мешает публикации" },
};

/** Порядок: сначала то, что мешает, затем предупреждения, затем готовое. */
const ORDER: Readonly<Record<PublishCheck["level"], number>> = { error: 0, warning: 1, ok: 2 };

interface Props {
  checks: PublishCheck[];
  /** «Вернуть элемент» шаблона товара; нет — кнопки нет (окно отката). */
  onRestore?: (kind: ProductRequiredKind) => Promise<void>;
}

export function PublishChecksList({ checks, onRestore }: Props) {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const builder = useBuilderLink();
  const sorted = [...checks].sort((a, b) => ORDER[a.level] - ORDER[b.level]);
  return (
    <ul className="flex flex-col gap-2">
      {sorted.map((check) => {
        const meta = LEVEL_META[check.level];
        const Icon = meta.icon;
        const link = checkLinkMeta(check);
        return (
          <li key={`${check.code}:${check.message}`} className="flex items-start gap-2">
            <Icon aria-hidden className={cn("mt-0.5 size-4 shrink-0", meta.className)} />
            <span className="sr-only">{meta.sr}: </span>
            <span className="type-body min-w-0 flex-1 text-on-surface">
              {check.message}
              {link && check.level !== "ok" ? (
                <Link
                  to={link.to(slug, builder)}
                  className="flex min-h-12 items-center font-medium text-primary hover:underline lg:inline-flex lg:min-h-0 lg:pl-1"
                >
                  {link.label} →
                </Link>
              ) : null}
              {check.issues && check.level !== "ok" ? <TemplateIssueList issues={check.issues} onRestore={onRestore} /> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
